import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../ui/Icon.jsx';
import './GlobalApiKeyManager.css';
import { ApiKeyManager } from '../../../services/ApiKeyManager.js';
import { ToolService } from '../../../services/ToolService.js';

// LLM Provider configurations (always shown)
const llmProviders = [
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'Models from OpenAI',
    website: 'https://platform.openai.com/api-keys',
    freeQuota: 'Pay as you go',
    status: 'Available',
    category: 'llm',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' }
    ]
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    description: 'DeepSeek Chat and other DeepSeek models',
    website: 'https://platform.deepseek.com/api_keys',
    freeQuota: 'Pay as you go',
    status: 'Available',
    category: 'llm',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' }
    ]
  }
];

// Fallback services if backend is unavailable
const fallbackServices = [
  {
    id: 'brave',
    name: 'Brave Search',
    description: 'Web search for all agents',
    website: 'https://api.search.brave.com/app/keys',
    freeQuota: '2,000 queries/month',
    status: 'Working',
    category: 'tool',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'BSAxxxxxxxxxxxxxxxxxxxxxxxx' }
    ]
  },
  {
    id: 'serpapi',
    name: 'SerpAPI (Google Search)',
    description: 'Google search results for all agents',
    website: 'https://serpapi.com/',
    freeQuota: '100 searches/month',
    status: 'Working'
  },
  {
    id: 'sendgrid',
    name: 'SendGrid',
    description: 'Email delivery service for all agents',
    website: 'https://sendgrid.com/',
    freeQuota: '100 emails/day',
    status: 'Working via backend proxy',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'SG.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' }
    ]
  },
  {
    id: 'mailgun',
    name: 'Mailgun',
    description: 'Email delivery service for all agents',
    website: 'https://mailgun.com/',
    freeQuota: '5,000 emails/month',
    status: 'Working via backend proxy',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'key-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
      { key: 'domain', label: 'Domain', type: 'text', placeholder: 'mg.yourdomain.com' }
    ]
  },
  {
    id: 'emailjs',
    name: 'EmailJS',
    description: 'Browser-based email service for all agents',
    website: 'https://emailjs.com/',
    freeQuota: '200 emails/month',
    status: 'Working',
    fields: [
      { key: 'serviceId', label: 'Service ID', type: 'password', placeholder: 'service_xxxxxxxx' },
      { key: 'templateId', label: 'Template ID', type: 'password', placeholder: 'template_xxxxxxxx' },
      { key: 'publicKey', label: 'Public Key', type: 'password', placeholder: 'xxxxxxxxxxxxxxx' }
    ]
  }
];

const GlobalApiKeyManager = ({ onClose }) => {
  const [keys, setKeys] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [testResults, setTestResults] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [toast, setToast] = useState(null);
  const dialogRef = useRef(null);
  const confirmationRef = useRef(null);
  const confirmationTriggerRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('button')?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);

  useEffect(() => {
    if (!confirmDialog) return undefined;
    const trigger = confirmationTriggerRef.current;
    const parentDialog = dialogRef.current;
    confirmationRef.current?.querySelector('button')?.focus();
    return () => {
      if (trigger?.isConnected && !trigger.disabled) trigger.focus();
      else parentDialog?.querySelector('button')?.focus();
    };
  }, [confirmDialog]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (confirmDialog) confirmDialog.onCancel();
        else closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const activeDialog = confirmDialog ? confirmationRef.current : dialogRef.current;
      const focusable = Array.from(activeDialog?.querySelectorAll(
        'button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]'
      ) || []).filter(element => element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) {
        event.preventDefault();
        activeDialog?.focus();
      } else if (event.shiftKey && (document.activeElement === first || !activeDialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !activeDialog.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [confirmDialog]);

  const [toolServices, setToolServices] = useState(fallbackServices);
  const [loading, setLoading] = useState(true);

  // Combine LLM providers with tool services (memoized to prevent infinite loops)
  const services = React.useMemo(() => [...llmProviders, ...toolServices], [toolServices]);

  // Load services from backend tools
  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoading(true);
        const tools = await ToolService.getAvailableTools();

        // Extract unique services that require API keys
        const serviceMap = new Map();

        tools.forEach(tool => {
          if (tool.requiresApiKey) {
            const provider = tool.requiresApiKey.provider;
            if (!serviceMap.has(provider)) {
              const service = {
                id: provider,
                name: tool.requiresApiKey.name || provider.charAt(0).toUpperCase() + provider.slice(1),
                description: tool.requiresApiKey.description || `API key for ${tool.name}`,
                website: tool.requiresApiKey.website || '#',
                freeQuota: tool.requiresApiKey.freeQuota || 'Check provider website',
                status: 'Working',
                fields: tool.requiresApiKey.fields || [
                  { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Enter API key...' }
                ]
              };
              serviceMap.set(provider, service);
            }
          }
        });

        const loadedServices = Array.from(serviceMap.values()).map(s => ({ ...s, category: 'tool' }));
        setToolServices(loadedServices.length > 0 ? loadedServices : fallbackServices);
      } catch (error) {
        console.error('[GlobalApiKeyManager] Failed to load services from backend, using fallback:', error);
        setToolServices(fallbackServices);
      } finally {
        setLoading(false);
      }
    };

    loadServices();
  }, []);

  // Load keys whenever services change
  useEffect(() => {
    const allKeys = {};
    services.forEach(service => {
      if (service.fields) {
        // Special case: if only one field named 'apiKey', load from just the provider name
        if (service.fields.length === 1 && service.fields[0].key === 'apiKey') {
          const key = ApiKeyManager.getApiKey(service.id);
          allKeys[service.id] = { [service.fields[0].key]: key || '' };
        } else {
          // True multi-field service (like SMTP)
          allKeys[service.id] = {};
          service.fields.forEach(field => {
            const key = ApiKeyManager.getApiKey(`${service.id}_${field.key}`);
            allKeys[service.id][field.key] = key || '';
          });
        }
      } else {
        // Single field service (legacy)
        const key = ApiKeyManager.getApiKey(service.id);
        allKeys[service.id] = key || '';
      }
    });
    setKeys(allKeys);
  }, [services]);

  const handleKeyChange = (serviceId, fieldKey, value) => {
    setKeys(prev => {
      if (fieldKey) {
        // Multi-field service
        return {
          ...prev,
          [serviceId]: {
            ...prev[serviceId],
            [fieldKey]: value
          }
        };
      } else {
        // Single field service (legacy)
        return {
          ...prev,
          [serviceId]: value
        };
      }
    });
  };

  const saveKey = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    const serviceName = service?.name;

    if (service.fields) {
      // Multi-field service
      const serviceKeys = keys[serviceId];
      let hasValidKeys = false;

      // Special case: if only one field named 'apiKey', save as just the provider name
      if (service.fields.length === 1 && service.fields[0].key === 'apiKey') {
        const value = serviceKeys?.[service.fields[0].key];
        if (value && value.trim()) {
          ApiKeyManager.setApiKey(serviceId, value.trim());
          hasValidKeys = true;
        } else {
          ApiKeyManager.deleteApiKey(serviceId);
        }
      } else {
        // True multi-field service (like SMTP with host, port, user, password)
        service.fields.forEach(field => {
          const value = serviceKeys?.[field.key];
          if (value && value.trim()) {
            ApiKeyManager.setApiKey(`${serviceId}_${field.key}`, value.trim());
            hasValidKeys = true;
          } else {
            ApiKeyManager.deleteApiKey(`${serviceId}_${field.key}`);
          }
        });
      }

      if (hasValidKeys) {
        showToast(`${serviceName} configuration saved globally!`, 'success');
      } else {
        showToast(`${serviceName} configuration removed!`, 'info');
      }
    } else {
      // Single field service (legacy)
      const key = keys[serviceId];
      if (key && key.trim()) {
        ApiKeyManager.setApiKey(serviceId, key.trim());
        showToast(`${serviceName} API key saved globally!`, 'success');
      } else {
        ApiKeyManager.deleteApiKey(serviceId);
        showToast(`${serviceName} API key removed!`, 'info');
      }
    }
    // Keys will be reloaded automatically via useEffect when services change
  };

  const removeKey = (serviceId) => {
    confirmationTriggerRef.current = document.activeElement;
    const service = services.find(s => s.id === serviceId);
    const serviceName = service?.name;
    setConfirmDialog({
      title: 'Remove Configuration',
      message: `Remove ${serviceName} configuration? This will affect all agents.`,
      onConfirm: () => {
        if (service.fields) {
          // Multi-field service
          service.fields.forEach(field => {
            ApiKeyManager.deleteApiKey(service.fields.length === 1 && field.key === 'apiKey' ? serviceId : `${serviceId}_${field.key}`);
          });
          setKeys(prev => ({
            ...prev,
            [serviceId]: {}
          }));
        } else {
          // Single field service
          ApiKeyManager.deleteApiKey(serviceId);
          setKeys(prev => ({
            ...prev,
            [serviceId]: ''
          }));
        }
        showToast('Configuration removed globally!', 'success');
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const testKey = async (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    const serviceKeys = keys[serviceId];

    // Handle multi-field services (like SendGrid, Mailgun)
    if (service?.fields) {
      const primaryField = service.fields.find(f => f.key === 'apiKey') || service.fields[0];
      const key = serviceKeys?.[primaryField.key];

      if (!key || typeof key !== 'string' || !key.trim()) {
        showToast(`Please enter ${primaryField.label} first`, 'warning');
        return;
      }

      setTestResults(prev => ({
        ...prev,
        [serviceId]: 'testing'
      }));

      try {
        const isValid = ApiKeyManager.validateApiKeyFormat(serviceId, key.trim());
        setTestResults(prev => ({
          ...prev,
          [serviceId]: isValid ? 'valid' : 'invalid'
        }));
      } catch (error) {
        setTestResults(prev => ({
          ...prev,
          [serviceId]: 'error'
        }));
      }
    } else {
      // Handle single-field services (legacy)
      const key = serviceKeys;

      if (!key || typeof key !== 'string' || !key.trim()) {
        showToast('Please enter an API key first', 'warning');
        return;
      }

      setTestResults(prev => ({
        ...prev,
        [serviceId]: 'testing'
      }));

      try {
        const isValid = ApiKeyManager.validateApiKeyFormat(serviceId, key.trim());
        setTestResults(prev => ({
          ...prev,
          [serviceId]: isValid ? 'valid' : 'invalid'
        }));
      } catch (error) {
        setTestResults(prev => ({
          ...prev,
          [serviceId]: 'error'
        }));
      }
    }

    // Clear test result after 3 seconds
    setTimeout(() => {
      setTestResults(prev => ({
        ...prev,
        [serviceId]: null
      }));
    }, 3000);
  };

  const toggleShowKey = (serviceId) => {
    setShowKeys(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const clearAllKeys = () => {
    confirmationTriggerRef.current = document.activeElement;
    setConfirmDialog({
      title: 'Clear All API Keys',
      message: 'Remove all stored API keys? This will affect all agents and cannot be undone.',
      onConfirm: () => {
        ApiKeyManager.clearAllKeys();
        // Trigger re-render by updating keys state
        setKeys({});
        showToast('All API keys removed!', 'success');
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  return createPortal(
    <div className="api-keys-modal">
      <div className="api-keys-backdrop" onClick={() => !confirmDialog && onClose()} aria-hidden="true" />
      <section
        className="api-keys-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="api-keys-title"
        aria-describedby="api-keys-description"
        inert={confirmDialog ? true : undefined}
        tabIndex={-1}
      >
        <header className="api-keys-header">
          <div className="api-keys-heading">
            <span className="api-keys-heading-icon"><Icon name="key" size={21} /></span>
            <div>
              <span className="api-keys-eyebrow">WORKSPACE SETTINGS</span>
              <h2 id="api-keys-title">Connected services</h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="api-keys-close" aria-label="Close connected services">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="api-keys-body">
          <p id="api-keys-description" className="api-keys-intro">
            Add your own API keys to give your agents access to AI models, search, and email. Saved keys are shared across agents in this browser.
          </p>
          <div className="api-keys-storage-note">
            <Icon name="shield" size={18} />
            <p>Keys are stored locally in your browser. When you use a service, its key may be sent through our backend proxy to the provider.</p>
          </div>

          {loading && <p role="status">Loading available tool connections…</p>}
          <div className="api-keys-services">
            {services.map(service => {
              const hasSavedKey = service.fields
                ? service.fields.some(field => ApiKeyManager.hasApiKey(service.fields.length === 1 && field.key === 'apiKey' ? service.id : `${service.id}_${field.key}`))
                : ApiKeyManager.hasApiKey(service.id);
              const hasValue = service.fields
                ? Object.values(keys[service.id] || {}).some(value => value?.trim())
                : Boolean(keys[service.id]?.trim());
              const checksConnection = false;
              const result = testResults[service.id];
              const fields = service.fields || [{ key: null, label: 'API key', type: 'password', placeholder: `Enter your ${service.name} API key` }];

              return (
                <section key={service.id} className="api-key-service" aria-labelledby={`api-key-service-${service.id}`}>
                  <div className="api-key-service-header">
                    <div>
                      <h3 id={`api-key-service-${service.id}`}>{service.name}</h3>
                      <p>{service.description}</p>
                    </div>
                    <span className={`api-key-status${hasSavedKey ? ' is-saved' : ''}`}>
                      {hasSavedKey && <Icon name="check" size={12} />}
                      {hasSavedKey ? 'Saved' : 'Not configured'}
                    </span>
                  </div>

                  <div className="api-key-fields">
                    {fields.map(field => {
                      const fieldId = field.key ? `${service.id}_${field.key}` : service.id;
                      const value = field.key ? keys[service.id]?.[field.key] || '' : keys[service.id] || '';
                      return (
                        <div key={fieldId} className="api-key-field">
                          <label htmlFor={`api-key-${fieldId}`}>{field.label}</label>
                          <div className="api-key-input-row">
                            <input
                              id={`api-key-${fieldId}`}
                              type={showKeys[fieldId] ? 'text' : field.type}
                              value={value}
                              onChange={event => handleKeyChange(service.id, field.key, event.target.value)}
                              placeholder={field.placeholder}
                              autoComplete="off"
                              spellCheck={false}
                            />
                            {field.type === 'password' && (
                              <button
                                type="button"
                                onClick={() => toggleShowKey(fieldId)}
                                className="api-key-visibility"
                                aria-label={`${showKeys[fieldId] ? 'Hide' : 'Show'} ${service.name} ${field.label}`}
                                aria-pressed={Boolean(showKeys[fieldId])}
                              >
                                {showKeys[fieldId] ? 'Hide' : 'Show'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="api-key-actions">
                    <button type="button" onClick={() => saveKey(service.id)} className="api-key-button is-primary" disabled={!hasValue}>Save key</button>
                    <button type="button" onClick={() => testKey(service.id)} className="api-key-button" disabled={!hasValue || result === 'testing'}>
                      {result === 'testing' ? 'Checking…' : checksConnection ? 'Test connection' : 'Check format'}
                    </button>
                    <button type="button" onClick={() => removeKey(service.id)} className="api-key-button is-remove" disabled={!hasSavedKey}>Remove</button>
                    <a href={service.website} target="_blank" rel="noopener noreferrer" className="api-key-provider-link">
                      Get a key <Icon name="arrowUpRight" size={14} />
                    </a>
                  </div>
                  <div className={`api-key-test-result${result ? ` is-${result}` : ''}`} role="status" aria-live="polite">
                    {result === 'testing' && 'Checking your key…'}
                    {result === 'valid' && (checksConnection ? 'Connection successful.' : 'Key format looks valid. The connection has not been tested.')}
                    {result === 'invalid' && (checksConnection ? 'Connection could not be verified. Check your key and try again.' : 'The key format could not be verified. Check your details.')}
                    {result === 'error' && 'The check could not be completed. Please try again.'}
                  </div>
                </section>
              );
            })}
          </div>
          <footer className="api-keys-footer">
            <div>
              <strong>Remove saved credentials</strong>
              <p>Agents will need new keys to use these services.</p>
            </div>
            <button type="button" onClick={clearAllKeys} className="api-key-button is-danger">Clear all keys</button>
          </footer>
        </div>
      </section>

      {confirmDialog && (
        <div className="api-key-confirmation-overlay">
          <section
            ref={confirmationRef}
            className="api-key-confirmation"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="api-key-confirmation-title"
            aria-describedby="api-key-confirmation-description"
            tabIndex={-1}
          >
            <span className="api-keys-heading-icon"><Icon name="key" size={22} /></span>
            <h3 id="api-key-confirmation-title">{confirmDialog.title}</h3>
            <p id="api-key-confirmation-description">{confirmDialog.message}</p>
            <div className="api-key-confirmation-actions">
              <button type="button" onClick={confirmDialog.onCancel} className="api-key-button">Cancel</button>
              <button type="button" onClick={confirmDialog.onConfirm} className="api-key-button is-danger-solid">Remove keys</button>
            </div>
          </section>
        </div>
      )}
      {toast && <div className={`api-keys-toast is-${toast.type}`} role="status" aria-live="polite">{toast.message}</div>}
    </div>,
    document.body
  );
};

export default GlobalApiKeyManager;
